import React, { createContext, useContext, useMemo } from 'react';
import { TaskComment, TaskCommentFieldAnchor } from '../../types';
import { TaskMode } from '../../taskTypes/module';
import { ValidatorComment } from '../../services/taskValidator';
import { taskCommentFieldAnchorKey } from '../../utils/taskCommentAnchors';

/**
 * A task view registers the tab (or nested tab) that owns a field path. The
 * shell can then select the right tab before the field's FieldCommentSlot
 * mounts and scrolls it into view.
 */
export interface TaskTabRegistration {
  id: string;
  fieldPathPrefixes: readonly string[];
  select: () => void;
}

export interface ReviewContextValue {
  mode: TaskMode;
  comments: TaskComment[];
  activeFieldPath: string | null;
  setActiveFieldPath: (path: string | null) => void;
  registerTaskTab: (registration: TaskTabRegistration) => () => void;
  navigateToField: (fieldPath: string) => void;
  /**
   * A `FieldCommentSlot` publishes its human-readable `label` for its field
   * path so shell copy (the footer's resubmit notice) can name fields the
   * way the form does. Latest registration for a path wins.
   */
  registerFieldLabel: (fieldPath: string, label: string) => void;
  openCommentComposer: (fieldPath: string, fieldAnchor?: TaskCommentFieldAnchor) => void;
  /**
   * Field-level ROOT comments only, keyed by stable anchor when available and
   * by legacy fieldPath otherwise. Replies are excluded so pin counts reflect
   * thread counts, not total utterances. Replies live under `commentsByRoot`.
   */
  commentsByField: Map<string, TaskComment[]>;
  /** Replies keyed by their parent root id, chronological ascending. */
  commentsByRoot: Map<string, TaskComment[]>;
  markAddressed: (commentId: string, addressed: boolean) => Promise<void> | void;
  /** Whether the current viewer can flip a root comment's resolved state.
   *  True for the task owner during work mode, for reviewers reviewing this
   *  task, and for admins viewing a real (non-preview) task. Reviewers/admins
   *  use this when the writer replied but didn't mark resolved and the
   *  response is satisfactory. */
  canResolveComments: boolean;
  pendingCommentId: string | null;

  /**
   * When non-null, the comment column swaps to a focused single-field view
   * showing that field's threads + composers. Triggered by clicking a field's
   * pin; cleared by the "Back to all comments" button.
   */
  focusedFieldPath: string | null;
  focusedFieldAnchor: TaskCommentFieldAnchor | null;
  setFocusedFieldPath: (path: string | null, fieldAnchor?: TaskCommentFieldAnchor) => void;

  /** Resolve an anchored comment against the current parsed result. Returns
   * null when the anchored requirement was deleted. */
  resolveCommentFieldPath: (comment: TaskComment) => string | null;

  /** Add a reply to a root comment. Reply rows share the root's fieldPath and
   *  inherit the task's CURRENT review round (not the root's) so the
   *  writer-resubmit gate can count "replied this round" correctly. */
  addReply: (rootCommentId: string, text: string) => Promise<void> | void;

  /** Author-only inline edit of a human comment's text (root or reply).
   *  The UI decides visibility; the server enforces via rules. */
  editComment: (commentId: string, text: string) => Promise<void> | void;

  /** Author-only delete of a human comment (root or reply). Firestore rules
   *  enforce the author check; the UI hides the affordance from non-authors. */
  deleteComment: (commentId: string) => Promise<void> | void;

  /** Current task.reviewRound — the column uses it to format round headers
   *  and reply-composer placeholder copy; gate helpers use it separately. */
  currentRound: number;

  /** Current task.ownerId — used so the column can label the writer's own
   *  replies with their real name even when the viewer is a non-admin. */
  ownerId: string | null;

  /** Current viewer's uid — lets the column surface the viewer's own name on
   *  their own replies while still collapsing other reviewers to the uniform
   *  "Reviewer" voice for non-admins. */
  currentUserUid: string | null;

  /** True when the viewer is an admin. Admins see the real reviewer name
   *  and provenance (human vs AI) on every comment for auditing; non-admins
   *  see a uniform "Reviewer" voice so AI and human reviewers are
   *  indistinguishable to the annotator. */
  isAdminViewer: boolean;

  /** Current task result, used to compare against per-comment rework
   *  snapshots. Only reviewers/admins get the UI affordance. */
  currentResult: unknown;
  canViewFieldDiffs: boolean;

  /**
   * Root comment ids that are currently blocking the writer from resubmitting
   * — i.e. unresolved current-round roots with no writer reply. Empty set
   * when the gate is either satisfied or not applicable (initial round,
   * review mode, preview mode).
   *
   * Threads use this to render a per-thread "resolve or reply to resubmit"
   * notice at the point of action. A task-level banner would force the
   * writer to hunt through the panel for which thread still needs them.
   */
  blockedRootIds: Set<string>;

  // ---- LLM comment moderation ----
  /** Whether the current user (reviewer or admin on this task's campaign) can
   *  approve / edit / delete LLM comments on this task. Drives visibility of
   *  the reviewer action row inside a CommentItem. */
  canModerateLlm: boolean;
  approveLlmComment: (commentId: string) => Promise<void> | void;
  editLlmComment: (commentId: string, text: string) => Promise<void> | void;
  deleteLlmComment: (commentId: string) => Promise<void> | void;

  // ---- Pre-submit validator (transient) ----
  /** Validator comments grouped by fieldPath. Lives in component state on
   *  TaskDetailPage and is cleared when the user re-runs Validate or
   *  submits. Empty map when validation hasn't run, when it returned no
   *  issues, or when this task type / campaign-binding doesn't enable
   *  validation. */
  validatorCommentsByField: Map<string, ValidatorComment[]>;
  /** Per-comment dismiss. Keyed by `${fieldPath}::${index}` since validator
   *  comments don't have stable ids. Dismissed comments stay hidden until
   *  the next Validate run replaces the whole batch. */
  dismissedValidatorIds: Set<string>;
  dismissValidatorComment: (fieldPath: string, index: number) => void;
  restoreValidatorComment: (fieldPath: string, index: number) => void;
}

const DEFAULT: ReviewContextValue = {
  mode: 'preview',
  comments: [],
  activeFieldPath: null,
  setActiveFieldPath: () => {},
  registerTaskTab: () => () => {},
  navigateToField: () => {},
  registerFieldLabel: () => {},
  openCommentComposer: () => {},
  commentsByField: new Map(),
  commentsByRoot: new Map(),
  markAddressed: () => {},
  canResolveComments: false,
  pendingCommentId: null,
  focusedFieldPath: null,
  focusedFieldAnchor: null,
  setFocusedFieldPath: () => {},
  resolveCommentFieldPath: (comment) => comment.fieldPath,
  addReply: () => {},
  editComment: () => {},
  deleteComment: () => {},
  currentRound: 0,
  ownerId: null,
  currentUserUid: null,
  isAdminViewer: false,
  currentResult: null,
  canViewFieldDiffs: false,
  canModerateLlm: false,
  approveLlmComment: () => {},
  editLlmComment: () => {},
  deleteLlmComment: () => {},
  blockedRootIds: new Set<string>(),
  validatorCommentsByField: new Map(),
  dismissedValidatorIds: new Set<string>(),
  dismissValidatorComment: () => {},
  restoreValidatorComment: () => {},
};

const Ctx = createContext<ReviewContextValue>(DEFAULT);

export function ReviewProvider({
  value,
  children,
}: {
  value: ReviewContextValue;
  children: React.ReactNode;
}) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useReviewContext(): ReviewContextValue {
  return useContext(Ctx);
}

export function useTaskNavigation(): Pick<ReviewContextValue, 'registerTaskTab' | 'navigateToField'> {
  const ctx = useReviewContext();
  return {
    registerTaskTab: ctx.registerTaskTab,
    navigateToField: ctx.navigateToField,
  };
}

export interface UseFieldCommentsResult {
  /** Root comments on this field (replies live in `commentsByRoot`). */
  comments: TaskComment[];
  unaddressedCount: number;
  isActive: boolean;
  isFocused: boolean;
  canComment: boolean;
  canHighlight: boolean;
  openComposer: () => void;
  activate: () => void;
  focus: () => void;
  /** Pre-submit validator comments anchored to this field, with dismissed
   *  ones already filtered out. Each entry carries its own `dismiss()`
   *  closure so callers don't have to track the original index. Empty when
   *  validation hasn't run, returned no issues, or the user dismissed
   *  everything for this field. */
  validatorComments: ValidatorRendered[];
}

/** A single validator comment ready for rendering — text + severity + the
 *  closure to dismiss this exact entry from view. */
export interface ValidatorRendered {
  comment: ValidatorComment;
  dismiss: () => void;
}

/**
 * Per-field hook used by `FieldCommentSlot` (and by modules if they want to
 * render custom indicators). Returns the root comments targeting this field,
 * the current interaction capabilities, and handlers to open the composer or
 * focus (swap the column to the single-field thread view) the field.
 */
export function useFieldComments(
  fieldPath: string,
  fieldAnchor?: TaskCommentFieldAnchor,
): UseFieldCommentsResult {
  const ctx = useReviewContext();
  const anchorKey = taskCommentFieldAnchorKey(fieldAnchor);
  const comments = useMemo(
    () => {
      const anchored = anchorKey ? ctx.commentsByField.get(anchorKey) ?? [] : [];
      const legacy = ctx.commentsByField.get(fieldPath) ?? [];
      if (anchored.length === 0) return legacy;
      if (legacy.length === 0) return anchored;
      const seen = new Set<string>();
      return [...anchored, ...legacy].filter((comment) => {
        if (seen.has(comment.id)) return false;
        seen.add(comment.id);
        return true;
      });
    },
    [anchorKey, ctx.commentsByField, fieldPath],
  );
  const unaddressedCount = useMemo(() => comments.filter((c) => !c.addressed).length, [comments]);
  const validatorComments = useMemo<ValidatorRendered[]>(() => {
    const all = ctx.validatorCommentsByField.get(fieldPath) ?? [];
    return all
      // Filter dismissed BEFORE pairing so the index in the closure stays
      // tied to the underlying state, not the rendered list. (Otherwise
      // dismissing the second-of-two-shown would dismiss the wrong entry
      // after the first dismissal collapsed the list.)
      .map((comment, i) => ({ comment, originalIndex: i }))
      .filter(({ originalIndex }) => !ctx.dismissedValidatorIds.has(`${fieldPath}::${originalIndex}`))
      .map(({ comment, originalIndex }) => ({
        comment,
        dismiss: () => ctx.dismissValidatorComment(fieldPath, originalIndex),
      }));
  }, [ctx, fieldPath]);
  return {
    comments,
    unaddressedCount,
    isActive: ctx.activeFieldPath === fieldPath,
    isFocused: ctx.focusedFieldPath === fieldPath,
    canComment: ctx.mode === 'review',
    canHighlight: ctx.mode !== 'preview',
    openComposer: () => ctx.openCommentComposer(fieldPath, fieldAnchor),
    activate: () => ctx.navigateToField(fieldPath),
    focus: () => ctx.setFocusedFieldPath(fieldPath, fieldAnchor),
    validatorComments,
  };
}
